import * as React from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import BaseInput, { BaseInputProps, SupportedInputType } from './BaseInput';
import ErrorMessage from './ErrorMessage';

// The differences between RegisteredInput props and BaseInput props:
// - `id` and `value` are required
// - `error`, `ariaDescribedby` should not be exposed on RegisteredInput
// - `FieldError['type']` excludes `maxLength`, `minLength`, and other HTML5 validations
//   which, instead of getting passed to RegisteredInput, get passed as config in `register`
type RegisteredInputProps = Exclude<
  BaseInputProps,
  'id' | 'value' | 'error' | 'ariaDescribedby' | FieldError['type']
> & {
  id: string;
  errorMessage?: string;
  value?: string;
  register: UseFormRegisterReturn;
};

const RegisteredInput: React.FunctionComponent<RegisteredInputProps> = ({
  errorMessage,
  value,
  id,
  type = SupportedInputType.text,
  centered = false,
  animateLabel = false,
  label,
  disabled = false,
  featureName,
  containerOverridesStyle,
  placeholder,
  register,
}) => {
  const errorId = `${id}-error`;
  const hasError = Boolean(errorMessage && errorMessage.length);
  const { onBlur: rOnBlur, onChange, ref, name } = register;

  const onBlur = React.useCallback(
    (event?: React.FocusEvent<HTMLInputElement>) => {
      if (event) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        rOnBlur(event);
      }
    },
    [rOnBlur],
  );

  return (
    <>
      <BaseInput
        value={value}
        ref={ref}
        id={id}
        type={type}
        name={name}
        error={hasError}
        ariaDescribedby={errorId}
        onBlur={onBlur}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onChange={onChange}
        centered={centered}
        animateLabel={animateLabel}
        label={label}
        disabled={disabled}
        featureName={featureName}
        placeholder={placeholder}
        containerOverridesStyle={containerOverridesStyle}
      />
      <ErrorMessage id={errorId} featureName={featureName} show={hasError}>
        {errorMessage}
      </ErrorMessage>
    </>
  );
};

export default RegisteredInput;
